import {useMemo, useState} from 'react'
import {Text as RNText, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {extractHeadersFromMarkdown} from '#/lib/markdown/extractHeaders'
import {useKawaiiMode} from '#/state/preferences/kawaii'
import {useSession} from '#/state/session'
import {DesktopSearch} from '#/view/shell/desktop/Search'
import {
  atoms as a,
  useGutters,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import {AppLanguageDropdown} from '#/components/AppLanguageDropdown'
import {CENTER_COLUMN_OFFSET} from '#/components/Layout'
import {InlineLinkText} from '#/components/Link'
import {TableOfContents} from '#/components/TableOfContents'
import {Text} from '#/components/Typography'
import {communityGuidelinesContent} from '#/content/community-guidelines'
import {copyrightPolicyContent} from '#/content/copyright-policy'
import {privacyPolicyContent} from '#/content/privacy-policy'
import {termsOfServiceContent} from '#/content/terms-of-service'
import {disclosure2257} from '#/content/2257'

interface PolicyRightNavProps {
  routeName: string
}

export function PolicyRightNav({routeName}: PolicyRightNavProps) {
  const t = useTheme()
  const {_} = useLingui()
  const {hasSession} = useSession()
  const kawaii = useKawaiiMode()
  const gutters = useGutters(['base', 0, 'base', 'wide'])
  const {rightNavVisible, centerColumnOffset, leftNavMinimal} =
    useLayoutBreakpoints()

  // Determine markdown content based on route
  const markdownContent = useMemo(() => {
    switch (routeName) {
      case 'PrivacyPolicy':
        return privacyPolicyContent
      case 'TermsOfService':
        return termsOfServiceContent
      case 'CommunityGuidelines':
        return communityGuidelinesContent
      case 'CopyrightPolicy':
        return copyrightPolicyContent
      case 'Disclosure2257':
        return disclosure2257
      default:
        return ''
    }
  }, [routeName])

  const headers = useMemo(
    () => extractHeadersFromMarkdown(markdownContent),
    [markdownContent],
  )

  const showTableOfContents =
    routeName !== 'Support' && routeName !== 'About' && routeName !== 'Privacy'

  const [legalOpen, setLegalOpen] = useState(false)

  const handleFeedbackClick = () => {
    if (typeof window !== 'undefined' && (window as any).formbricks) {
      ;(window as any).formbricks.track('feedback')
    }
  }

  if (!rightNavVisible) {
    return null
  }

  const width = centerColumnOffset ? 250 : 300

  return (
    <View
      style={[
        gutters,
        a.gap_lg,
        web({
          position: 'fixed',
          left: '50%',
          transform: [
            {
              translateX: 300 + (centerColumnOffset ? CENTER_COLUMN_OFFSET : 0),
            },
            ...a.scrollbar_offset.transform,
          ],
          width: width + gutters.paddingLeft,
          maxHeight: '100%',
          overflowY: 'auto',
          paddingBottom: 120,
        }),
      ]}>
      {hasSession ? <DesktopSearch /> : <View style={{height: 60}} />}

      <View style={[a.gap_sm, a.mb_2xl]}>
        <Text style={[a.leading_snug, t.atoms.text_contrast_low]}>
          {hasSession && (
            <>
              <Text
                onPress={handleFeedbackClick}
                style={[
                  {color: '#fd7c21'},
                  web({className: 'Feedback', cursor: 'pointer'}),
                ]}
                role="link">
                {_(msg`Feedback`)}
              </Text>
              {' • '}
            </>
          )}
          <InlineLinkText to="/support/privacy" label={_(msg`Privacy`)}>
            {_(msg`Privacy`)}
          </InlineLinkText>
          {' • '}
          <InlineLinkText label={_(msg`Help`)} to="/support">
            {_(msg`Help`)}
          </InlineLinkText>
          {' • '}
          <RNText
            onPress={() => setLegalOpen(o => !o)}
            style={[{color: '#fd7c21'}, web({cursor: 'pointer'})]}
            role="button">
            {_(msg`Legal`)}
          </RNText>
          {' • '}
          <InlineLinkText label={_(msg`About`)} to="/support/about">
            {_(msg`About`)}
          </InlineLinkText>
        </Text>
        {legalOpen && (
          <View style={[a.gap_xs]}>
            <InlineLinkText
              to="/support/community-guidelines"
              label={_(msg`Community Guidelines`)}>
              {_(msg`Community Guidelines`)}
            </InlineLinkText>
            <InlineLinkText to="/support/tos" label={_(msg`Terms of Service`)}>
              {_(msg`Terms of Service`)}
            </InlineLinkText>
            <InlineLinkText
              to="/support/privacy-policy"
              label={_(msg`Privacy Policy`)}>
              {_(msg`Privacy Policy`)}
            </InlineLinkText>
            <InlineLinkText
              to="/support/copyright"
              label={_(msg`Copyright Policy`)}>
              {_(msg`Copyright Policy`)}
            </InlineLinkText>
            <InlineLinkText to="/support/2257" label={_(msg`2257 Disclosure`)}>
              {_(msg`2257 Disclosure`)}
            </InlineLinkText>
          </View>
        )}
      </View>

      {showTableOfContents && <TableOfContents headers={headers} />}

      {kawaii && (
        <Text style={[t.atoms.text_contrast_medium, {marginTop: 12}]}>
          <Trans>
            Logo by{' '}
            <InlineLinkText
              label={_(msg`Logo by @sawaratsuki.bsky.social`)}
              to="/profile/sawaratsuki.bsky.social">
              @sawaratsuki.bsky.social
            </InlineLinkText>
          </Trans>
        </Text>
      )}
    </View>
  )
}
